import {
  titleUI,
  yesNoUI,
  yesNoSchema,
  currentOrPastDateUI,
} from 'platform/forms-system/src/js/web-component-patterns';
import {
  currentYear,
  parseISODate,
} from 'platform/forms-system/src/js/helpers';
import VaDateField from 'platform/forms-system/src/js/web-component-fields/VaDateField';
import VaMemorableDateField from 'platform/forms-system/src/js/web-component-fields/VaMemorableDateField';
import { isValidDate } from 'platform/forms-system/src/js/utilities/validations';
import { ConditionalRevealYesNoField } from '../components/ConditionalRevealRadioField';
import { applicationInfoFields, FORM_21_4502 } from '../definitions/constants';

const { VETERAN_DISABILITY_COMPENSATION: V } = FORM_21_4502;

const validateDisabilityCompensationDateApplied = (errors, value, formData) => {
  if (formData?.applicationInfo?.veteranDisabilityCompensation !== true) {
    return;
  }

  const { day, month, year } = parseISODate(value);
  const hasAnyDatePart = [day, month, year].some(Boolean);
  const hasCompleteDate = [day, month, year].every(Boolean);

  if (!value || !hasAnyDatePart) {
    errors.addError(V.ERROR_DATE_APPLIED_REQUIRED);
    return;
  }

  if (!hasCompleteDate) {
    errors.addError(V.ERROR_DATE_APPLIED_INCOMPLETE);
    return;
  }

  if (
    Number(year) < 1900 ||
    Number(year) > currentYear ||
    !isValidDate(day, month, year)
  ) {
    errors.addError(V.ERROR_DATE_APPLIED_INVALID);
  }
};

const buildVeteranDisabilityCompensationPage = ({
  DateField,
  monthSelect = false,
  resetSubmittedStateOnReveal = false,
}) => {
  const veteranDisabilityCompensationUI = yesNoUI({
    title: V.QUESTION_APPLIED,
    labels: V.LABELS,
    required: () => true,
    errorMessages: {
      required: V.ERROR_APPLIED,
    },
  });

  if (resetSubmittedStateOnReveal) {
    veteranDisabilityCompensationUI['ui:options'] = {
      ...(veteranDisabilityCompensationUI['ui:options'] || {}),
      conditionalRevealValues: [true],
    };
    veteranDisabilityCompensationUI['ui:webComponentField'] =
      ConditionalRevealYesNoField;
  }

  const dateUI = currentOrPastDateUI({
    title: V.DATE_APPLIED,
    required: formData =>
      formData?.applicationInfo?.veteranDisabilityCompensation === true,
    errorMessages: {
      pattern: V.ERROR_DATE_APPLIED_INVALID,
      required: V.ERROR_DATE_APPLIED_REQUIRED,
    },
  });

  return {
    uiSchema: {
      'ui:options': {
        preserveHiddenData: true,
        forceDivWrapper: true,
      },
      ...titleUI(V.TITLE),
      [applicationInfoFields.parentObject]: {
        [applicationInfoFields.veteranDisabilityCompensation]:
          veteranDisabilityCompensationUI,
        [applicationInfoFields.appliedDisabilityCompensationDate]: {
          ...dateUI,
          'ui:options': {
            ...(dateUI['ui:options'] || {}),
            hideIf: formData =>
              formData?.applicationInfo?.veteranDisabilityCompensation !== true,
            ...(monthSelect ? { monthSelect: true } : {}),
          },
          'ui:webComponentField': DateField,
          'ui:errorMessages': {
            pattern: V.ERROR_DATE_APPLIED_INVALID,
            required: V.ERROR_DATE_APPLIED_REQUIRED,
          },
          'ui:validations': [validateDisabilityCompensationDateApplied],
        },
      },
    },
    schema: {
      type: 'object',
      properties: {
        [applicationInfoFields.parentObject]: {
          type: 'object',
          properties: {
            [applicationInfoFields.veteranDisabilityCompensation]: yesNoSchema,
            [applicationInfoFields.appliedDisabilityCompensationDate]: {
              type: 'string',
            },
          },
          required: [applicationInfoFields.veteranDisabilityCompensation],
        },
      },
    },
  };
};

export const veteranDisabilityCompensationNewFlow =
  buildVeteranDisabilityCompensationPage({
    DateField: VaMemorableDateField,
    monthSelect: true,
    resetSubmittedStateOnReveal: true,
  });

export default buildVeteranDisabilityCompensationPage({
  DateField: VaDateField,
});
